import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { Currency, IPrice } from '@/api/types/public';
import {
  EDIT_PRESCRIPTION_SUB_STEPS,
  LENS_PROCESS_COMPONENT_CLOSE_NAME,
  LENS_PROCESS_COMPONENT_LEAVE_EVENT_NAME,
} from '@/app/_lens-components/constants';
import {
  BrandItem,
  CoatingItem,
  ColorItem,
  CustomerPrescription,
  ELensViewAreaType,
  IScanInfo,
  LensGoodsItem,
  LensIndexItem,
  LensColorCategory,
  LensShowItem,
  LensShowResponse,
} from '@/app/_lens-components/types';
import { resolveLensTypeTrackingFields } from '@/app/_lens-components/tracking';
import { getApiPrescriptionType, shouldApplyCoatingSelection } from '@/app/_lens-components/utils';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { env } from '@/utils/env';
import { genPrice } from '@/utils/price';
import { getRouteCnName } from '@/utils/route-metadata';
import { TrackEventFunction } from 'index';
import { create } from 'zustand';
import { getBlockFlags, getFeatureValueUsed } from '@/utils/feature-value';

// 定义一个trackEvent类型
interface BrandExpand {
  [key: string]: boolean;
}

interface LensGoodsSensorsData {
  product_view_uuid: string;
  shop_sku: string;
  business_sku: string;
  product_sku: string;
  product_id: string;
  product_name: string;
  entrance_edit_lens_scene: string;
  edit_lens_type: string;
  product_class_name: string;
  frame_price: number;
  current_frame_price: number;
  currency: string;
  prescription_enter_type?: string;
}
export interface PrescriptionsMethod {
  id?: number;
  name: string;
  img?: string;
  description?: string;
  type?: number;
  img_url?: string;
  module?: number;
  title?: string;
  status?: number;
  sort?: number;
  is_only_read?: number;
  option_img_url?: null;
}
export interface PrescriptionsMethodsData {
  methods: PrescriptionsMethod[];
  prescriptionTypesCount: {
    'Single Vision'?: number;
    Progressive?: number;
    'Reading Glasses'?: number;
    'Sunglasses Prescription'?: number;
    'Frame Only'?: number;
    'Non-Prescription'?: number;
    'Sunglasses Non-Prescription'?: number;
  };
}
export interface SelectedPrescription extends CustomerPrescription {
  /** 用户保存的处方图片路径；当前列表接口的生成类型尚未包含此返回字段。 */
  prescriptions_image?: string;
  isUpdate?: boolean;
}
export interface EditingPrescriptionDraft {
  id: SelectedPrescription['id'];
  name: string;
  prescription: IPrescriptionOptions;
  prescription_type?: string;
  prescriptions_image?: string;
}
export interface LensProcessState {
  // 是否是无处方类型（包括无处方、太阳镜无处方）
  isNonPrescriptionType: boolean;
  // 是否是积分兑换商品
  isRedeem: boolean;
  // 商品基本信息
  lensGoodsItem: LensGoodsItem;
  // 神策埋点商品数据（包括一进入配镜流程就确定的product_view_uuid、entrance_edit_lens_scene）
  lensGoodsSensorsData: LensGoodsSensorsData;

  // 处方类型  注意：这里会包含老化二级的类型，在对用户展示处方类型以及调接口时需要特殊处理
  prescriptionType: EPrescriptionType;
  // 处方类型名称
  presTypeName: string;
  // 处方类型选项名称(跟presTypeName的区别是：如果是老花镜的话，则为子项的名称)
  presTypeOptionName: string;
  // 处方
  prescription: IPrescriptionOptions;
  // 成品老花镜选择索引
  readerSelectedIndex: number;
  // 处方图片
  prescriptionsImage: string;
  // 处方识别信息
  scanInfo: IScanInfo;

  // 处方编辑相关
  selectedMethod: PrescriptionsMethod | null;
  editPresStep: number;
  selectedPrescription: SelectedPrescription | null;
  editingPrescriptionDraft: EditingPrescriptionDraft | null;

  // 镜片类型相关
  lensTypeColorData: LensShowResponse;
  selectedLensType: LensShowItem | null;
  selectedBrand: BrandItem | null;
  selectedColorCategory: LensColorCategory | null;
  selectedColorItem: ColorItem | null;
  brandExpand: BrandExpand;
  prescriptionFillMethodsData: PrescriptionsMethodsData;
  // 专门用于埋点的镜片类型字段（仅针对普通镜片类型）
  lensTypeForTracking: string;
  // 折射率相关
  lensIndexTip: string;
  selectedLensIndexItem: LensIndexItem | null;

  // 高级渐进镜相关
  selectedAdvancedLensIndexItem: (LensIndexItem & { lens_type_name?: string }) | null;
  selectedLensViewAreaType: ELensViewAreaType | null;
  progressiveViewArea:
    | GetResponseType<ApiResponseDefinition['GET:/api/lens/view_area']>['view_area']
    | null;

  // 镀膜相关
  coatingList: CoatingItem[];
  selectedCoating: CoatingItem | null;

  // 价格相关
  goodsFramePriceInfo: IPrice;
  totalPrice: IPrice;
  lensTypePriceInfo: IPrice;
  lensIndexPriceInfo: IPrice;
  coatingPriceInfo: IPrice;

  // UI状态
  /** 当前配镜流程步骤组件列表（顺序与 nowStep 下标一致） */
  allComponent: string[];
  nowStep: number;
  nowComponent: string;
  btnDisable: boolean;
  stepBtnText: string;
  priceDescDiagFlag: boolean;
  showMemberLabel: boolean;
  hasIndexPrice: boolean;
  campaignTypeIdList: string[];
  ttqEventId: string;
  currency: Currency;
  prescriptionScrollTop: number;
  addToCartLoading: boolean;
  isLoadingLensShow: boolean;
  showChooseColor: boolean;

  // 埋点方法
  trackEventFn: TrackEventFunction | null;

  // 操作方法
  setIsNonPrescriptionType: (isNonPrescriptionType: boolean) => void;
  setIsRedeem: (isRedeem: boolean) => void;
  setLensGoodsItem: (lensGoodsItem: LensGoodsItem) => void;
  setLensGoodsSensorsData: (lensGoodsSensorsData: LensGoodsSensorsData) => void;
  setPrescriptionType: (prescriptionType: EPrescriptionType) => void;
  setPresTypeName: (presTypeName: string) => void;
  setPresTypeOptionName: (presTypeOptionName: string) => void;
  setPrescription: (prescription: IPrescriptionOptions) => void;
  setReaderSelectedIndex: (readerSelectedIndex: number) => void;
  setPrescriptionsImage: (prescriptionsImage: string) => void;
  setScanInfo: (scanInfo: IScanInfo) => void;
  setSelectedMethod: (selectedMethod: PrescriptionsMethod | null) => void;
  setEditPresStep: (editPresStep: number) => void;
  setSelectedPrescription: (selectedPrescription: SelectedPrescription | null) => void;
  setEditingPrescriptionDraft: (editingPrescriptionDraft: EditingPrescriptionDraft | null) => void;
  setLensTypeColorData: (lensTypeColorData: LensShowResponse) => void;
  setSelectedLensType: (selectedLensType: LensShowItem | null) => void;
  setSelectedBrand: (selectedBrand: BrandItem | null) => void;
  setSelectedColorCategory: (selectedColorCategory: LensColorCategory | null) => void;
  setSelectedColorItem: (selectedColorItem: ColorItem | null) => void;
  setLensTypeForTracking: (lensTypeForTracking: string) => void;
  setLensIndexTip: (lensIndexTip: string) => void;
  setSelectedLensIndexItem: (selectedLensIndexItem: LensIndexItem | null) => void;
  setSelectedAdvancedLensIndexItem: (selectedAdvancedLensIndexItem: LensIndexItem | null) => void;
  setSelectedLensViewAreaType: (selectedLensViewAreaType: ELensViewAreaType | null) => void;
  setProgressiveViewArea: (
    progressiveViewArea:
      | GetResponseType<ApiResponseDefinition['GET:/api/lens/view_area']>['view_area']
      | null,
  ) => void;
  setCoatingList: (coatingList: CoatingItem[]) => void;
  setSelectedCoating: (selectedCoating: CoatingItem | null) => void;
  setTotalPrice: (totalPrice: IPrice) => void;
  setGoodsFramePriceInfo: (goodsFramePriceInfo: IPrice) => void;
  setLensTypePriceInfo: (lensTypePriceInfo: IPrice) => void;
  setLensIndexPriceInfo: (lensIndexPriceInfo: IPrice) => void;
  setCoatingPriceInfo: (coatingPriceInfo: IPrice) => void;
  setAllComponent: (allComponent: string[]) => void;
  setNowStep: (nowStep: number) => void;
  setNowComponent: (nowComponent: string) => void;
  setBtnDisable: (btnDisable: boolean) => void;
  setStepBtnText: (stepBtnText: string) => void;
  setPriceDescDiagFlag: (priceDescDiagFlag: boolean) => void;
  togglePriceDescDiagFlag: () => void;
  setShowMemberLabel: (showMemberLabel: boolean) => void;
  setHasIndexPrice: (hasIndexPrice: boolean) => void;
  setCampaignTypeIdList: (campaignTypeIdList: string[]) => void;
  setTtqEventId: (ttqEventId: string) => void;
  setCurrency: (currency: Currency) => void;
  setTrackEventFn: (trackEventFn: TrackEventFunction) => void;
  setPrescriptionScrollTop: (prescriptionScrollTop: number) => void;
  setAddToCartLoading: (addToCartLoading: boolean) => void;
  setIsLoadingLensShow: (isLoadingLensShow: boolean) => void;
  setBrandExpand: (brandExpand: BrandExpand) => void;
  setShowChooseColor: (showChooseColor: boolean) => void;
  setPrescriptionFillMethodsData: (data: PrescriptionsMethodsData) => void;
  // 复合操作
  initLensType: () => void;
  initLensIndex: () => void;
  initLensViewArea: () => void;
  initCoating: () => void;
  resetStore: () => void; // 新增重置store的方法

  // 埋点
  SDLensTypePageNextClick: (extraParams?: Record<string, string | number>) => void;
  SDLensIndexPageNextClick: () => void;
  SDSendLeaveEvent: (leave_type: string) => void;
  SDEditLensPageView: () => void;
  SDEditLensPageClose: () => void;
  SDAddToCartBtnClick: () => void;
  SDAddToCartResult: (params: { is_success: boolean; service_code: string; msg?: string }) => void;
  SDAddToCartBaseEvent: (params: {
    eventName: string;
    is_success?: boolean;
    service_code?: string;
    msg?: string;
  }) => void;
  sendPoints: (requestData: any) => boolean;
}

export const useLensProcessStore = create<LensProcessState>((set, get) => ({
  // 是否是无处方类型（包括无处方、太阳镜无处方）
  isNonPrescriptionType: false,
  // 是否是积分兑换商品
  isRedeem: false,
  // 商品基本信息
  lensGoodsItem: null,
  // 神策埋点商品数据（包括一进入配镜流程就确定的product_view_uuid、entrance_edit_lens_scene）
  lensGoodsSensorsData: null,

  // 处方类型
  prescriptionType: null,
  presTypeName: '', // 处方类型名称
  // 处方类型选项名称(跟presTypeName的区别是：如果是老花镜的话，则为子项的名称)
  presTypeOptionName: '',
  // 处方
  prescription: getEmptyPrescription(),
  // 成品老花镜选择索引
  readerSelectedIndex: -1,
  // 处方图片
  prescriptionsImage: '',
  // 处方识别信息
  scanInfo: null,

  // 处方编辑相关
  selectedMethod: null,
  editPresStep: EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS,
  selectedPrescription: null,
  editingPrescriptionDraft: null,

  // 镜片类型相关
  lensTypeColorData: [],
  selectedLensType: null,
  selectedBrand: null,
  selectedColorCategory: null,
  selectedColorItem: null,
  brandExpand: {},
  prescriptionFillMethodsData: {
    methods: [],
    prescriptionTypesCount: {
      'Single Vision': 0,
      Progressive: 0,
      'Reading Glasses': 0,
      'Sunglasses Prescription': 0,
      'Frame Only': 0,
      'Non-Prescription': 0,
      'Sunglasses Non-Prescription': 0,
    },
  },
  // 专门用于埋点的镜片类型字段
  lensTypeForTracking: '',

  // 折射率相关
  lensIndexTip: '',
  selectedLensIndexItem: null,

  // 高级渐进镜相关
  selectedAdvancedLensIndexItem: null,
  selectedLensViewAreaType: null,
  progressiveViewArea: null,

  // 镀膜相关
  coatingList: [],
  selectedCoating: null,

  // 价格相关
  totalPrice: { currency: '', sell_price: '0.00', price: '0.00' },
  goodsFramePriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
  lensTypePriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
  lensIndexPriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
  coatingPriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },

  // UI状态
  allComponent: [],
  nowStep: 0,
  nowComponent: '',
  btnDisable: true,
  stepBtnText: 'Next',
  priceDescDiagFlag: false,
  showMemberLabel: false,
  hasIndexPrice: false,
  campaignTypeIdList: [],
  ttqEventId: '',
  currency: null,
  trackEventFn: null,
  prescriptionScrollTop: 0,
  addToCartLoading: false,
  isLoadingLensShow: false,
  showChooseColor: false,
  // 基本操作方法
  setIsNonPrescriptionType: (isNonPrescriptionType) => set({ isNonPrescriptionType }),
  setIsRedeem: (isRedeem) => set({ isRedeem }),
  setLensGoodsItem: (lensGoodsItem) => set({ lensGoodsItem }),
  setLensGoodsSensorsData: (lensGoodsSensorsData) => set({ lensGoodsSensorsData }),
  setPrescriptionType: (prescriptionType) => set({ prescriptionType }),
  setPresTypeName: (presTypeName) => set({ presTypeName }),
  setPresTypeOptionName: (presTypeOptionName) => set({ presTypeOptionName }),
  setPrescription: (prescription) => set({ prescription }),
  setReaderSelectedIndex: (readerSelectedIndex) => set({ readerSelectedIndex }),
  setPrescriptionsImage: (prescriptionsImage) => set({ prescriptionsImage }),
  setScanInfo: (scanInfo) => set({ scanInfo }),
  setSelectedMethod: (selectedMethod) => set({ selectedMethod }),
  setEditPresStep: (editPresStep) => set({ editPresStep }),
  setSelectedPrescription: (selectedPrescription) => set({ selectedPrescription }),
  setEditingPrescriptionDraft: (editingPrescriptionDraft) => set({ editingPrescriptionDraft }),
  setLensTypeColorData: (lensTypeColorData) => set({ lensTypeColorData }),
  setSelectedLensType: (selectedLensType) =>
    set({
      selectedLensType,
      // 同时设置埋点专用字段
      lensTypeForTracking: selectedLensType?.sd_lens_type || selectedLensType?.lens_type || '',
    }),
  setSelectedBrand: (selectedBrand) => set({ selectedBrand }),
  setSelectedColorCategory: (selectedColorCategory) => set({ selectedColorCategory }),
  setSelectedColorItem: (selectedColorItem) => set({ selectedColorItem }),
  setLensTypeForTracking: (lensTypeForTracking) => set({ lensTypeForTracking }),
  setLensIndexTip: (lensIndexTip) => set({ lensIndexTip }),
  setSelectedLensIndexItem: (selectedLensIndexItem) => set({ selectedLensIndexItem }),
  setSelectedAdvancedLensIndexItem: (selectedAdvancedLensIndexItem) =>
    set({ selectedAdvancedLensIndexItem }),
  setSelectedLensViewAreaType: (selectedLensViewAreaType) => set({ selectedLensViewAreaType }),
  setProgressiveViewArea: (progressiveViewArea) => set({ progressiveViewArea }),
  setCoatingList: (coatingList) => set({ coatingList }),
  setSelectedCoating: (selectedCoating) => set({ selectedCoating }),
  setTotalPrice: (totalPrice) => set({ totalPrice }),
  setGoodsFramePriceInfo: (goodsFramePriceInfo) => set({ goodsFramePriceInfo }),
  setLensTypePriceInfo: (lensTypePriceInfo) => set({ lensTypePriceInfo }),
  setLensIndexPriceInfo: (lensIndexPriceInfo) => set({ lensIndexPriceInfo }),
  setCoatingPriceInfo: (coatingPriceInfo) => set({ coatingPriceInfo }),
  setAllComponent: (allComponent) => set({ allComponent }),
  setNowStep: (nowStep) => set({ nowStep }),
  setNowComponent: (nowComponent) => set({ nowComponent }),
  setBtnDisable: (btnDisable) => set({ btnDisable }),
  setStepBtnText: (stepBtnText) => set({ stepBtnText }),
  setPriceDescDiagFlag: (priceDescDiagFlag) => set({ priceDescDiagFlag }),
  togglePriceDescDiagFlag: () => set((state) => ({ priceDescDiagFlag: !state.priceDescDiagFlag })),
  setShowMemberLabel: (showMemberLabel) => set({ showMemberLabel }),
  setHasIndexPrice: (hasIndexPrice) => set({ hasIndexPrice }),
  setCampaignTypeIdList: (campaignTypeIdList) => set({ campaignTypeIdList }),
  setTtqEventId: (ttqEventId) => set({ ttqEventId }),
  setCurrency: (currency) => set({ currency }),
  setTrackEventFn: (trackEventFn) => set({ trackEventFn }),
  setPrescriptionScrollTop: (prescriptionScrollTop) => set({ prescriptionScrollTop }),
  setAddToCartLoading: (addToCartLoading) => set({ addToCartLoading }),
  setIsLoadingLensShow: (isLoadingLensShow) => set({ isLoadingLensShow }),
  setBrandExpand: (brandExpand) => set({ brandExpand }),
  setShowChooseColor: (showChooseColor) => set({ showChooseColor }),
  setPrescriptionFillMethodsData: (prescriptionFillMethodsData) =>
    set({ prescriptionFillMethodsData }),
  // 复合操作
  initLensType: () =>
    set({
      selectedLensType: null,
      selectedBrand: null,
      selectedColorCategory: null,
      selectedColorItem: null,
      lensTypeForTracking: '',
    }),

  initLensIndex: () =>
    set({
      selectedLensIndexItem: null,
    }),

  initLensViewArea: () =>
    set({
      selectedLensViewAreaType: null,
      selectedAdvancedLensIndexItem: null,
    }),

  initCoating: () =>
    set({
      selectedCoating: null,
      coatingPriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
    }),
  // 新增重置store的方法
  resetStore: () => {
    set({
      isNonPrescriptionType: false,
      isRedeem: false,
      lensGoodsItem: null,
      lensGoodsSensorsData: null,
      prescriptionType: null,
      presTypeName: '',
      presTypeOptionName: '',
      prescription: getEmptyPrescription(),
      readerSelectedIndex: -1,
      prescriptionsImage: '',
      scanInfo: null,
      selectedMethod: null,
      editPresStep: EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS,
      selectedPrescription: null,
      editingPrescriptionDraft: null,
      lensTypeColorData: [],
      selectedLensType: null,
      selectedBrand: null,
      selectedColorCategory: null,
      selectedColorItem: null,
      lensTypeForTracking: '',
      lensIndexTip: '',
      selectedLensIndexItem: null,
      selectedAdvancedLensIndexItem: null,
      selectedLensViewAreaType: null,
      progressiveViewArea: null,
      coatingList: [],
      selectedCoating: null,
      totalPrice: { currency: '', sell_price: '0.00', price: '0.00' },
      goodsFramePriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
      lensTypePriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
      lensIndexPriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
      coatingPriceInfo: { currency: '', sell_price: '0.00', price: '0.00' },
      allComponent: [],
      nowStep: 0,
      nowComponent: '',
      btnDisable: true,
      stepBtnText: 'Next',
      priceDescDiagFlag: false,
      showMemberLabel: false,
      hasIndexPrice: false,
      campaignTypeIdList: [],
      ttqEventId: '',
      currency: null,
      prescriptionScrollTop: 0,
      addToCartLoading: false,
      isLoadingLensShow: false,
      brandExpand: {},
      showChooseColor: false,
      trackEventFn: null,
    });
    logger.warn('SDEditLensPageClose--resetStore-0');
  },

  // 埋点方法
  SDLensTypePageNextClick: (extraParams: Record<string, string | number> = {}) => {
    try {
      const state = get();

      if (!state.trackEventFn) return;

      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: state.selectedLensType,
        brand: state.selectedBrand,
        lensTypeForTracking: state.lensTypeForTracking,
      });

      state.trackEventFn(
        {
          eventName: 'LensTypePage_Next_Click',
          properties: {
            ...(state.lensGoodsSensorsData || {}),
            prescription_type_name: state.presTypeOptionName,
            prescription_type: getApiPrescriptionType(state.prescriptionType),
            ...lensTypeTrackingFields,
            lens_color: state.selectedColorItem?.color_name,
            frame_price: parseFloat(state.goodsFramePriceInfo?.price),
            current_frame_price: parseFloat(state.goodsFramePriceInfo?.sell_price),
            lens_price: parseFloat(state.lensTypePriceInfo?.price),
            current_lens_price: parseFloat(state.lensTypePriceInfo?.sell_price),
            currency: state.currency?.name,
            lens_color_category_name: state.selectedColorCategory?.title ?? '',
            ...extraParams,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  },

  SDLensIndexPageNextClick: () => {
    try {
      const state = get();
      if (!state.trackEventFn) return;

      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: state.selectedLensType,
        brand: state.selectedBrand,
        lensTypeForTracking: state.lensTypeForTracking,
      });

      state.trackEventFn(
        {
          eventName: 'LensIndexPage_Next_Click',
          properties: {
            ...(state.lensGoodsSensorsData || {}),
            prescription_type_name: state.presTypeOptionName,
            prescription_type: getApiPrescriptionType(state.prescriptionType),
            ...lensTypeTrackingFields,
            lens_color: state.selectedColorItem?.color_name,
            frame_price: parseFloat(state.goodsFramePriceInfo?.price),
            current_frame_price: parseFloat(state.goodsFramePriceInfo?.sell_price),
            lens_price: parseFloat(state.lensTypePriceInfo?.price),
            current_lens_price: parseFloat(state.lensTypePriceInfo?.sell_price),
            currency: state.currency?.name,

            lens_id: `${state.selectedLensIndexItem.id}`,
            lens_index_name: state.selectedLensIndexItem.index_name,
            lens_index: state.selectedLensIndexItem.refractive_index,
            lens_code: state.selectedLensIndexItem.code,
            lens_index_price: parseFloat(state.lensIndexPriceInfo?.price),
            current_lens_index_price: parseFloat(state.lensIndexPriceInfo?.sell_price),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  },

  SDSendLeaveEvent: (leave_type: string) => {
    try {
      const state = get();
      if (!state.trackEventFn) return;

      const baseProperties = {
        leave_type,
        ...(state.lensGoodsSensorsData || {}),
      };

      const otherProperties: Record<string, any> = {};

      let colorItem = null;
      let lensIndexItem = null;
      if (state.selectedAdvancedLensIndexItem) {
        colorItem = state.selectedAdvancedLensIndexItem;
        lensIndexItem = state.selectedAdvancedLensIndexItem;
      } else {
        if (state.selectedColorItem) {
          colorItem = state.selectedColorItem;
        }
        if (state.selectedLensIndexItem) {
          lensIndexItem = state.selectedLensIndexItem;
        }
      }

      if (state.prescriptionType) {
        otherProperties.prescription_type_name = state.presTypeOptionName;
        otherProperties.prescription_type = getApiPrescriptionType(state.prescriptionType);
      }
      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: state.selectedLensType,
        brand: state.selectedBrand,
        advancedLensIndexItem: state.selectedAdvancedLensIndexItem,
        lensTypeForTracking: state.lensTypeForTracking,
      });
      otherProperties.lens_type_name = lensTypeTrackingFields.lens_type_name;
      otherProperties.lens_color = colorItem?.lens_color;
      otherProperties.lens_id = lensIndexItem?.id ? `${lensIndexItem.id}` : '';

      logger.warn(
        'SDSendLeaveEvent ===LensIndexPage_Leave',
        state.nowComponent,
        otherProperties,
        state.selectedLensIndexItem,
        state.selectedColorItem,
        state.selectedLensType,
      );

      const eventType = LENS_PROCESS_COMPONENT_LEAVE_EVENT_NAME[state.nowComponent];
      if (!eventType) return;

      state.trackEventFn(
        {
          eventName: eventType,
          properties: {
            ...baseProperties,
            ...otherProperties,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  },

  SDEditLensPageView: () => {
    try {
      const state = get();
      if (!state.trackEventFn) return;

      const baseProperties = {
        ...(state.lensGoodsSensorsData || {}),
      };

      const currentProcess = LENS_PROCESS_COMPONENT_CLOSE_NAME[state.nowComponent];

      state.trackEventFn(
        {
          eventName: 'EditLensPage_View',
          properties: {
            ...baseProperties,
            current_process: currentProcess,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  },

  SDEditLensPageClose: () => {
    try {
      const state = get();
      logger.warn('SDEditLensPageClose-1 trackEventFn 是否存在', !!state.trackEventFn);
      if (!state.trackEventFn) return;

      const baseProperties = {
        ...(state.lensGoodsSensorsData || {}),
      };

      const currentProcess = LENS_PROCESS_COMPONENT_CLOSE_NAME[state.nowComponent];

      state.trackEventFn(
        {
          eventName: 'EditLensPage_Close',
          properties: {
            ...baseProperties,
            current_process: currentProcess,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  },

  SDAddToCartBtnClick: () => {
    const state = get();
    state.SDAddToCartBaseEvent({
      eventName: 'AddToCartBtn_Click',
    });
  },

  SDAddToCartResult: ({ is_success, service_code, msg }) => {
    const state = get();
    state.SDAddToCartBaseEvent({
      eventName: 'AddToCart_Result',
      is_success,
      service_code,
      msg,
    });
  },

  SDAddToCartBaseEvent: ({
    eventName,
    is_success,
    service_code,
    msg,
  }: {
    eventName: string;
    is_success?: boolean;
    service_code?: string;
    msg?: string;
  }) => {
    try {
      const state = get();
      if (!state.trackEventFn) return;

      const baseProperties = {
        ...(state.lensGoodsSensorsData || {}),
        add_cart_scene: '配镜流程加购',
        is_product_lens: state.lensGoodsItem?.product?.product_lens === 1,
        campaign_type_id_list: state.campaignTypeIdList,
        refer_page_name: getRouteCnName(window.location.pathname),
        refer_url_path: window.location.href,
        is_glass: true,
        currency: state.currency?.name,
        dollar_exchange_rate: parseFloat(state.currency?.exchange_rate),
        // 价值归因
        ...getFeatureValueUsed(state.lensGoodsItem?.product?.shop_sku || ''),
        // 呈现BI报表 - 推荐位信息
        ...getBlockFlags(),
      };

      const otherProperties: Record<string, any> = {
        prescription_type_name: state.presTypeOptionName,
        prescription_type: getApiPrescriptionType(state.prescriptionType),
      };

      otherProperties.is_success = is_success;
      otherProperties.service_code = service_code;

      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: state.selectedLensType,
        brand: state.selectedBrand,
        advancedLensIndexItem: state.selectedAdvancedLensIndexItem,
        lensTypeForTracking: state.lensTypeForTracking,
      });

      let colorItem = null;
      let lensIndexItem = null;
      if (state.selectedAdvancedLensIndexItem) {
        colorItem = state.selectedAdvancedLensIndexItem;
        lensIndexItem = state.selectedAdvancedLensIndexItem;
      } else {
        if (state.selectedColorItem) {
          colorItem = state.selectedColorItem;
        }
        if (state.selectedLensIndexItem) {
          lensIndexItem = state.selectedLensIndexItem;
        }
      }
      Object.assign(otherProperties, lensTypeTrackingFields);

      if (colorItem && state.lensTypePriceInfo) {
        otherProperties.lens_color = colorItem.lens_color;

        otherProperties.lens_price = parseFloat(state.lensTypePriceInfo?.price);
        otherProperties.current_lens_price = parseFloat(state.lensTypePriceInfo?.sell_price);
      }

      if (lensIndexItem && state.lensIndexPriceInfo) {
        otherProperties.lens_id = `${lensIndexItem.id}`;
        otherProperties.lens_index = lensIndexItem.refractive_index;
        otherProperties.lens_code = lensIndexItem.code;
        otherProperties.lens_index_name = lensIndexItem.index_name;
        otherProperties.lens_index_price = parseFloat(state.lensIndexPriceInfo?.price);
        otherProperties.current_lens_index_price = parseFloat(state.lensIndexPriceInfo?.sell_price);
      }

      const applyCoating = shouldApplyCoatingSelection(
        state.prescriptionType,
        state.selectedColorItem,
        { isFrameProcess: state.prescriptionType === EPrescriptionType.FRAME_ONLY },
      );
      if (applyCoating && state.selectedCoating && state.coatingPriceInfo) {
        otherProperties.coating_id = state.selectedCoating.id ? `${state.selectedCoating.id}` : '';
        otherProperties.coating_type_name = state.selectedCoating.name;
        otherProperties.coating_price = parseFloat(state.coatingPriceInfo?.price);
        otherProperties.current_coating_price = parseFloat(state.coatingPriceInfo?.sell_price);
      }

      if (state.goodsFramePriceInfo) {
        otherProperties.product_price = parseFloat(state.goodsFramePriceInfo?.price);
        otherProperties.current_product_price = parseFloat(state.goodsFramePriceInfo?.sell_price);
      }
      const basePriceObj = genPrice(
        state.lensGoodsItem?.product?.price_info,
        env.NEXT_PUBLIC_DEFAULT_CURRENCY,
      );
      otherProperties.base_current_product_price = parseFloat(basePriceObj.sell_price);
      otherProperties.msg = msg;

      state.trackEventFn(
        {
          eventName,
          properties: {
            ...baseProperties,
            ...otherProperties,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  },

  sendPoints: (requestData) => {
    try {
      const state = get();
      if (!state.trackEventFn) return false;
      logger.warn(
        'sendPoints====',
        state.lensGoodsItem,
        requestData,
        state.totalPrice,
        state.ttqEventId,
      );

      state.trackEventFn(
        undefined,
        [
          ProviderType.GA4,
          ProviderType.FACEBOOK,
          ProviderType.BING,
          ProviderType.TIKTOK,
          ProviderType.APPLOVIN,
          ProviderType.TWITTER,
          ProviderType.SNAPCHAT,
          ProviderType.SNAPCHAT_CONVERSION,
        ],
        {
          lensGoodsItem: state.lensGoodsItem,
          requestData,
          totalPrice: state.totalPrice,
          ttqEventId: state.ttqEventId,
        },
        'addToCart.success',
      );

      return true;
    } catch (e) {
      logger.error('👀', e);
      return false;
    }
  },
}));
